import type {
  TenantAdminClinicianOperationV1,
  TenantAdminClinicianOperationsSortDirection,
  TenantAdminClinicianOperationsSortKey,
  TenantAdminOutboundQuestionAnswerTypeV1,
  TenantAdminOutboundQuestionBranchConditionV1,
  TenantAdminOutboundQuestionSkipLogicV1,
  TenantAdminOutboundQuestionnaireQuestionInputV1,
  TenantAdminPatientCommandStatus,
  TenantAdminPatientOperationsSortDirection,
  TenantAdminPatientOperationsSortKey,
  TenantAdminPatientPortalRoute,
  TenantAdminSummaryFeatureFlagV1,
  TenantAdminSummaryPatientOperationV1,
  TenantAdminSummaryResponseV1,
} from "@amplecare/contracts";

import {
  humanizeWorkspaceKey,
  resolvePatientAccessModeLabel,
  resolveWorkforceSessionRoleLabel,
} from "../shell-core.js";

export function buildAdminCommandKey(
  value: string,
  fallbackValue: string,
): string {
  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "");

  return normalizedValue.length > 0 ? normalizedValue : fallbackValue;
}

export type AdminAnalyticsSnapshot = TenantAdminSummaryResponseV1["summary"]["analytics"];
export type AdminAnalyticsRangeKey = "today" | "7d" | "30d" | "custom";

export type AdminAnalyticsRangeSelection = {
  departmentCode: string | null;
  endDate: string;
  rangeKey: AdminAnalyticsRangeKey;
  startDate: string;
};

export function formatAdminDateInputValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function parseAdminDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);

  if (!match) {
    return null;
  }

  const year = Number.parseInt(match[1] ?? "", 10);
  const month = Number.parseInt(match[2] ?? "", 10) - 1;
  const day = Number.parseInt(match[3] ?? "", 10);
  const parsed = new Date(year, month, day);

  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function addAdminDateDays(value: Date, dayCount: number): Date {
  const next = new Date(value);

  next.setDate(next.getDate() + dayCount);

  return next;
}

export function resolveAdminAnalyticsRangeSelection(
  rangeKey: AdminAnalyticsRangeKey,
  now = new Date(),
  departmentCode: string | null = null,
): AdminAnalyticsRangeSelection {
  const endDate = formatAdminDateInputValue(now);

  if (rangeKey === "today") {
    return {
      departmentCode,
      endDate,
      rangeKey,
      startDate: endDate,
    };
  }

  const daySpan = rangeKey === "30d" ? 29 : 6;

  return {
    departmentCode,
    endDate,
    rangeKey,
    startDate: formatAdminDateInputValue(addAdminDateDays(now, -daySpan)),
  };
}

export function buildAdminAnalyticsSummaryRequest(
  selection: AdminAnalyticsRangeSelection,
) {
  const startDate = parseAdminDateInputValue(selection.startDate);
  const endDate = parseAdminDateInputValue(selection.endDate);
  const safeStartDate = startDate ?? new Date();
  const safeEndDate = endDate ?? safeStartDate;
  const orderedStartDate =
    safeStartDate.getTime() <= safeEndDate.getTime()
      ? safeStartDate
      : safeEndDate;
  const orderedEndDate =
    safeStartDate.getTime() <= safeEndDate.getTime()
      ? safeEndDate
      : safeStartDate;

  orderedStartDate.setHours(0, 0, 0, 0);
  orderedEndDate.setHours(0, 0, 0, 0);

  return {
    analyticsDepartmentCode: selection.departmentCode?.trim() || null,
    analyticsEndAt: addAdminDateDays(orderedEndDate, 1).toISOString(),
    analyticsStartAt: orderedStartDate.toISOString(),
  };
}

export function sampleAdminAnalyticsSeries<T>(
  series: readonly T[],
  maxPointCount = 7,
): T[] {
  if (series.length <= maxPointCount) {
    return [...series];
  }

  const lastIndex = series.length - 1;
  const selectedIndexes = new Set<number>();

  for (let index = 0; index < maxPointCount; index += 1) {
    selectedIndexes.add(Math.round((index * lastIndex) / (maxPointCount - 1)));
  }

  return [...selectedIndexes]
    .sort((left, right) => left - right)
    .map((index) => series[index])
    .filter((point): point is T => point != null);
}

export function formatAdminSummaryTimestamp(value: string | null): string {
  if (!value) {
    return "Not recorded yet";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

export function paginateRows<RowValue>(
  rows: RowValue[],
  pageIndex: number,
  pageSize: number,
): RowValue[] {
  const startIndex = Math.max(0, pageIndex) * pageSize;

  return rows.slice(startIndex, startIndex + pageSize);
}

export type AdminOperationalSubjectRow = {
  accessLabel: string;
  accessSyncLastErrorMessage: string | null;
  accessSyncStatus: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressStreet: string | null;
  addressZip: string | null;
  alternateEmergencyContactPhone: string | null;
  archiveNotes: string | null;
  archiveReasonKey: string | null;
  archivedAt: string | null;
  archivedByReference: string | null;
  clinicianUserId: string;
  contactWindowText: string | null;
  dateOfBirth: string | null;
  departmentCode: string | null;
  displayName: string;
  emailAddress: string | null;
  emergencyContactPhone: string | null;
  familyName: string | null;
  focusArea: "patient" | "clinician";
  givenName: string | null;
  homePhoneNumber: string | null;
  id: string;
  identifier: string | null;
  lastUpdatedAt: string | null;
  lifecycleVersion: number;
  membershipStatus: string | null;
  nextAppointmentText: string | null;
  patientId: string | null;
  phoneNumber: string | null;
  preArchiveStatus: TenantAdminPatientCommandStatus | null;
  pendingDocumentCount: number;
  assignedPatientCount: number;
  isProtectedOwner: boolean;
  protectedOwnerLabel: string | null;
  leadingClinicianLabel: string | null;
  leadingClinicianUserId: string | null;
  onboardingInitialFlowComplete: boolean;
  portalAuthStatus: string;
  portalEntryPointRoute: TenantAdminPatientPortalRoute;
  portalStateUpdatedAt: string | null;
  programLabel: string | null;
  reminderWakeUpTime: string | null;
  remindersEnabled: boolean;
  requiresAttention: boolean;
  roleLabel: string;
  secondaryPhoneNumber: string | null;
  sex: "female" | "male" | null;
  siteId: string | null;
  siteLabel: string;
  statusLabel: string;
  subjectKey: string;
  supportText: string;
  summaryGeneratedAt: string | null;
  summaryPolicyVersion: string | null;
  summaryRefreshStatus: string | null;
  teamKey: string | null;
  teamName: string | null;
  unreadMessageCount: number;
  workPhoneNumber: string | null;
};

export type AdminPreviewPatientCareType =
  | "Synced"
  | "Chronic"
  | "Procedure"
  | "Pain Mgmt";

export type AdminPreviewPatientRow = AdminOperationalSubjectRow & {
  careTypeLabel: AdminPreviewPatientCareType;
};

export type AdminPatientQuickFilter =
  | "all"
  | "attention"
  | "active"
  | "inactive"
  | "archived";

export function isAdminPatientArchived(
  row: Pick<AdminOperationalSubjectRow, "archivedAt" | "statusLabel">,
): boolean {
  return (
    row.archivedAt != null ||
    row.statusLabel.trim().toLowerCase() === "archived"
  );
}

export function matchesAdminPatientQuickFilter(
  row: Pick<
    AdminOperationalSubjectRow,
    "archivedAt" | "requiresAttention" | "statusLabel"
  >,
  filter: AdminPatientQuickFilter,
): boolean {
  const normalizedStatus = row.statusLabel.trim().toLowerCase();
  const isArchived = isAdminPatientArchived(row);
  const isInactive =
    !isArchived &&
    (normalizedStatus === "inactive" || normalizedStatus === "retired");

  if (filter === "attention") {
    return !isArchived && row.requiresAttention;
  }

  if (filter === "active") {
    return !isArchived && !isInactive;
  }

  if (filter === "inactive") {
    return isInactive;
  }

  if (filter === "archived") {
    return isArchived;
  }

  return !isArchived;
}

export function buildAdminPatientLifecycleIdempotencyKey(
  action: "archive" | "restore",
  patientId: string,
  nonce = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
): string {
  const normalizedPatientId = buildAdminCommandKey(patientId, "patient");
  const normalizedNonce = buildAdminCommandKey(nonce, "request");

  return `admin-patient-${action}-${normalizedPatientId}-${normalizedNonce}`.slice(
    0,
    128,
  );
}

export const adminClinicianStatusOptions = [
  "active",
  "inactive",
  "suspended",
] as const;
export const adminOperationalStatusOptions = [
  "draft",
  "active",
  "inactive",
  "retired",
] as const;
export const adminPatientStatusOptions = [
  "draft",
  "active",
  "inactive",
] as const satisfies readonly TenantAdminPatientCommandStatus[];
export const adminConnectorFamilyOptions = [
  "mssql_direct",
  "fhir_r4",
  "epic",
  "custom",
] as const;
export const adminConnectorStatusOptions = [
  "draft",
  "active",
  "paused",
  "retired",
] as const;
export const adminConnectorSourceAuthorityOptions = [
  "incremental",
  "snapshot_authoritative",
  "mixed",
] as const;

export type AdminClinicianCrudStatus = (typeof adminClinicianStatusOptions)[number];
export type AdminOperationalCrudStatus =
  (typeof adminOperationalStatusOptions)[number];
export type AdminPatientCrudStatus = (typeof adminPatientStatusOptions)[number];
export type AdminConnectorFamily = (typeof adminConnectorFamilyOptions)[number];
export type AdminConnectorCrudStatus = (typeof adminConnectorStatusOptions)[number];
export type AdminConnectorSourceAuthority =
  (typeof adminConnectorSourceAuthorityOptions)[number];

export function resolveAdminClinicianCrudStatus(input: {
  membershipStatus: string | null;
  statusLabel: string;
}): AdminClinicianCrudStatus {
  if (input.membershipStatus === "suspended") {
    return "suspended";
  }

  if (
    input.membershipStatus === "revoked" ||
    input.statusLabel.trim().toLowerCase() === "inactive"
  ) {
    return "inactive";
  }

  return "active";
}

export type AdminCrudFormState =
  | {
      entityKind: "patient";
      mode: "create" | "update";
      addressCity: string;
      addressState: string;
      addressStreet: string;
      addressZip: string;
      alternateEmergencyContactPhone: string;
      contactWindowText: string;
      dateOfBirth: string;
      displayName: string;
      emailAddress: string;
      emergencyContactPhone: string;
      expectedLifecycleVersion: number | null;
      familyName: string;
      givenName: string;
      homePhoneNumber: string;
      identifier: string;
      leadingClinicianUserId: string;
      onboardingState: "complete" | "open";
      patientId: string;
      phoneNumber: string;
      programLabel: string;
      secondaryPhoneNumber: string;
      sex: "" | "female" | "male";
      siteId: string;
      status: AdminPatientCrudStatus;
      workPhoneNumber: string;
    }
  | {
      entityKind: "clinician";
      mode: "create" | "update";
      clinicianUserId: string;
      departmentCode: string;
      displayName: string;
      emailAddress: string;
      isProtectedOwner: boolean;
      phoneNumber: string;
      protectedOwnerLabel: string | null;
      previewProfileKey: string;
      teamKey: string;
      testLoginEmail: string;
      testLoginPassword: string;
      status: AdminClinicianCrudStatus;
    }
  | {
      entityKind: "department";
      mode: "create" | "update";
      departmentCode: string;
      displayName: string;
      siteCode: string;
      status: AdminOperationalCrudStatus;
    }
  | {
      entityKind: "team";
      mode: "create" | "update";
      departmentCode: string;
      displayName: string;
      status: AdminOperationalCrudStatus;
      teamKey: string;
    }
  | {
      entityKind: "connector";
      mode: "create" | "update";
      connectorFamily: AdminConnectorFamily;
      connectorId: string;
      departmentCode: string;
      displayName: string;
      metadataSummary: string;
      researchPackSlug: string;
      siteCode: string;
      sourceAuthorityMode: AdminConnectorSourceAuthority;
      sourceSystemKey: string;
      status: AdminConnectorCrudStatus;
      teamKey: string;
    }
  | {
      entityKind: "clinician" | "department" | "team" | "connector";
      mode: "retire";
      displayName: string;
      targetKey: string;
    };

export type AdminReviewQueueRow = {
  count: number;
  id: string;
  latestSignalAt: string | null;
  latestSignalText: string;
  ownerLabel: string;
  priorityLabel: "High" | "Medium" | "Low";
  queueLabel: string;
  sourceArea: string;
  supportingText: string;
};

export function resolveAdminPrioritySortValue(
  priorityLabel: "High" | "Medium" | "Low",
) {
  if (priorityLabel === "High") {
    return 3;
  }

  if (priorityLabel === "Medium") {
    return 2;
  }

  return 1;
}

export function resolveAdminTimestampSortValue(value: string | null): number {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);

  return Number.isNaN(parsed) ? 0 : parsed;
}

export function buildAdminPatientOperationalRows(
  patients: readonly TenantAdminSummaryPatientOperationV1[],
): AdminOperationalSubjectRow[] {
  return patients.map((patient) => ({
    accessLabel:
      patient.archivedAt != null
        ? "Archived record"
        : patient.requiresAttention
          ? "Attention needed"
          : "Active record",
    accessSyncLastErrorMessage: patient.accessSyncLastErrorMessage,
    accessSyncStatus: patient.accessSyncStatus,
    addressCity: patient.addressCity,
    addressState: patient.addressState,
    addressStreet: patient.addressStreet,
    addressZip: patient.addressZip,
    alternateEmergencyContactPhone: patient.alternateEmergencyContactPhone,
    archiveNotes: patient.archiveNotes,
    archiveReasonKey: patient.archiveReasonKey,
    archivedAt: patient.archivedAt,
    archivedByReference: patient.archivedByReference,
    clinicianUserId: patient.leadingClinicianUserId ?? "",
    contactWindowText: patient.contactWindowText ?? null,
    dateOfBirth: patient.dateOfBirth,
    departmentCode: null,
    displayName: patient.displayName,
    emailAddress: patient.emailAddress ?? null,
    emergencyContactPhone: patient.emergencyContactPhone,
    familyName: patient.familyName,
    focusArea: "patient",
    givenName: patient.givenName,
    homePhoneNumber: patient.homePhoneNumber,
    id: `patient-${patient.patientId}`,
    identifier: patient.identifier,
    lastUpdatedAt: patient.lastUpdatedAt,
    lifecycleVersion: patient.lifecycleVersion,
    leadingClinicianLabel: patient.leadingClinicianLabel,
    membershipStatus: null,
    nextAppointmentText: patient.nextAppointmentText,
    onboardingInitialFlowComplete: patient.onboardingInitialFlowComplete,
    patientId: patient.patientId,
    phoneNumber: patient.phoneNumber ?? null,
    preArchiveStatus: patient.preArchiveStatus,
    pendingDocumentCount: patient.pendingDocumentCount,
    assignedPatientCount: 0,
    isProtectedOwner: false,
    protectedOwnerLabel: null,
    portalAuthStatus: patient.portalAuthStatus,
    portalEntryPointRoute: patient.portalEntryPointRoute,
    portalStateUpdatedAt: patient.portalStateUpdatedAt,
    programLabel: patient.programLabel,
    reminderWakeUpTime: patient.reminderWakeUpTime,
    remindersEnabled: patient.remindersEnabled,
    requiresAttention: patient.requiresAttention,
    roleLabel: "Patient",
    secondaryPhoneNumber: patient.secondaryPhoneNumber,
    sex: patient.sex,
    siteId: patient.siteId,
    siteLabel: patient.leadingClinicianLabel ?? "Clinician unassigned",
    statusLabel: patient.statusLabel,
    subjectKey: patient.patientId,
    summaryGeneratedAt: patient.summaryGeneratedAt,
    summaryPolicyVersion: patient.summaryPolicyVersion,
    summaryRefreshStatus: patient.summaryRefreshStatus,
    teamKey: null,
    teamName: null,
    leadingClinicianUserId: patient.leadingClinicianUserId,
    supportText: [
      patient.identifier,
      patient.phoneNumber ? `phone ${patient.phoneNumber}` : "",
      patient.emailAddress ? `email ${patient.emailAddress}` : "",
      patient.givenName,
      patient.familyName,
      patient.addressCity,
      patient.addressState,
      patient.contactWindowText
        ? `contact ${patient.contactWindowText}`
        : "",
      patient.statusLabel,
      patient.leadingClinicianLabel,
      patient.summaryRefreshStatus
        ? `summary ${humanizeWorkspaceKey(patient.summaryRefreshStatus).toLowerCase()}`
        : "summary pending",
      patient.onboardingInitialFlowComplete
        ? "onboarding complete"
        : "onboarding open",
      `portal ${humanizeWorkspaceKey(patient.portalAuthStatus).toLowerCase()}`,
      patient.remindersEnabled
        ? `wake ${patient.reminderWakeUpTime ?? "unset"}`
        : "reminders off",
      patient.nextAppointmentText,
      patient.pendingDocumentCount > 0
        ? `${patient.pendingDocumentCount} pending docs`
        : "",
      patient.accessSyncStatus
        ? `access sync ${humanizeWorkspaceKey(patient.accessSyncStatus).toLowerCase()}`
        : "",
      patient.archiveReasonKey
        ? `archive reason ${humanizeWorkspaceKey(patient.archiveReasonKey).toLowerCase()}`
        : "",
      patient.unreadMessageCount > 0
        ? `${patient.unreadMessageCount} unread`
        : "",
    ]
      .filter((part): part is string => Boolean(part && part.length > 0))
      .join(" | "),
    unreadMessageCount: patient.unreadMessageCount,
    workPhoneNumber: patient.workPhoneNumber,
  }));
}

export function buildAdminClinicianOperationalRows(
  clinicians: readonly TenantAdminClinicianOperationV1[],
): AdminOperationalSubjectRow[] {
  return clinicians.map((clinician) => ({
    accessLabel: humanizeWorkspaceKey(clinician.membershipStatus),
    accessSyncLastErrorMessage: null,
    accessSyncStatus: null,
    addressCity: null,
    addressState: null,
    addressStreet: null,
    addressZip: null,
    alternateEmergencyContactPhone: null,
    archiveNotes: null,
    archiveReasonKey: null,
    archivedAt: null,
    archivedByReference: null,
    clinicianUserId: clinician.clinicianUserId,
    contactWindowText: null,
    dateOfBirth: null,
    departmentCode: clinician.departmentCode,
    displayName: clinician.displayName,
    emailAddress: clinician.emailAddress,
    emergencyContactPhone: null,
    familyName: null,
    focusArea: "clinician",
    givenName: null,
    homePhoneNumber: null,
    id: `clinician-${clinician.clinicianUserId}`,
    identifier: clinician.membershipId,
    lastUpdatedAt: clinician.lastUpdatedAt,
    lifecycleVersion: 1,
    leadingClinicianLabel: null,
    membershipStatus: clinician.membershipStatus,
    nextAppointmentText: null,
    onboardingInitialFlowComplete: false,
    patientId: null,
    preArchiveStatus: null,
    pendingDocumentCount: 0,
    assignedPatientCount: clinician.assignedPatientCount,
    phoneNumber: clinician.phoneNumber,
    portalAuthStatus: "not-started",
    portalEntryPointRoute: "overview",
    portalStateUpdatedAt: null,
    isProtectedOwner: clinician.isProtectedOwner,
    protectedOwnerLabel: clinician.protectedOwnerLabel,
    programLabel: clinician.departmentDisplayName,
    reminderWakeUpTime: null,
    remindersEnabled: false,
    requiresAttention:
      clinician.principalStatus !== "active" ||
      clinician.membershipStatus !== "active",
    roleLabel: clinician.isProtectedOwner
      ? clinician.protectedOwnerLabel ?? "Platform owner"
      : humanizeWorkspaceKey(clinician.primaryRoleKey),
    secondaryPhoneNumber: null,
    sex: null,
    siteId: null,
    siteLabel:
      clinician.departmentDisplayName ??
      clinician.siteDisplayName ??
      "Tenant-wide",
    statusLabel: humanizeWorkspaceKey(clinician.principalStatus),
    subjectKey: clinician.clinicianUserId,
    summaryGeneratedAt: null,
    summaryPolicyVersion: null,
    summaryRefreshStatus: null,
    supportText: [
      clinician.clinicianUserId,
      clinician.emailAddress,
      clinician.phoneNumber,
      clinician.departmentDisplayName,
      clinician.teamDisplayName,
      clinician.isProtectedOwner
        ? (clinician.protectedOwnerLabel ?? "protected owner")
        : "",
      `membership ${humanizeWorkspaceKey(clinician.membershipStatus).toLowerCase()}`,
      `access ${resolvePatientAccessModeLabel(clinician.patientAccessMode).toLowerCase()}`,
    ]
      .filter((part): part is string => Boolean(part && part.length > 0))
      .join(" | "),
    teamKey: clinician.teamKey,
    teamName: clinician.teamDisplayName,
    leadingClinicianUserId: null,
    unreadMessageCount: 0,
    workPhoneNumber: null,
  }));
}

export function buildAdminOperationalSubjectRows(
  summary: TenantAdminSummaryResponseV1["summary"] | null,
  focusArea: "patient" | "clinician",
): AdminOperationalSubjectRow[] {
  if (
    focusArea === "patient" &&
    (summary?.patientOperations?.length ?? 0) > 0
  ) {
    return buildAdminPatientOperationalRows(summary?.patientOperations ?? []);
  }

  return (summary?.workforceSubjects ?? [])
    .filter((subject) =>
      focusArea === "patient"
        ? subject.roleKeys.includes("patient") || subject.patientId != null
        : subject.roleKeys.includes("clinician"),
    )
    .map((subject) => ({
      accessLabel: resolvePatientAccessModeLabel(subject.patientAccessMode),
      accessSyncLastErrorMessage: null,
      accessSyncStatus: null,
      addressCity: null,
      addressState: null,
      addressStreet: null,
      addressZip: null,
      alternateEmergencyContactPhone: null,
      archiveNotes: null,
      archiveReasonKey: null,
      archivedAt: null,
      archivedByReference: null,
      clinicianUserId: subject.clinicianUserId,
      contactWindowText: null,
      dateOfBirth: null,
      departmentCode: null,
      displayName:
        subject.displayName ??
        (focusArea === "patient"
          ? (subject.patientId ?? humanizeWorkspaceKey(subject.subjectKey))
          : subject.clinicianUserId),
      emailAddress: null,
      emergencyContactPhone: null,
      familyName: null,
      focusArea,
      givenName: null,
      homePhoneNumber: null,
      id: `${focusArea}-${subject.subjectKey}`,
      identifier: subject.patientId,
      lastUpdatedAt: null,
      lifecycleVersion: 1,
      leadingClinicianLabel: null,
      membershipStatus: null,
      nextAppointmentText: null,
      onboardingInitialFlowComplete: false,
      patientId: subject.patientId,
      phoneNumber: null,
      preArchiveStatus: null,
      pendingDocumentCount: 0,
      assignedPatientCount: 0,
      isProtectedOwner: false,
      protectedOwnerLabel: null,
      portalAuthStatus: "not-started",
      portalEntryPointRoute: "overview",
      portalStateUpdatedAt: null,
      programLabel: null,
      reminderWakeUpTime: null,
      remindersEnabled: true,
      requiresAttention: false,
      roleLabel: resolveWorkforceSessionRoleLabel(subject.roleKeys),
      secondaryPhoneNumber: null,
      sex: null,
      siteId: subject.siteId,
      siteLabel: subject.siteId
        ? humanizeWorkspaceKey(subject.siteId)
        : "Tenant-wide",
      statusLabel: resolvePatientAccessModeLabel(subject.patientAccessMode),
      subjectKey: subject.subjectKey,
      summaryGeneratedAt: null,
      summaryPolicyVersion: null,
      summaryRefreshStatus: null,
      teamKey: null,
      teamName: null,
      leadingClinicianUserId: null,
      supportText:
        focusArea === "patient"
          ? [
              subject.patientId ? `Patient ${subject.patientId}` : "",
              subject.subjectKey,
            ]
              .filter((part) => part.length > 0)
              .join(" | ")
          : [subject.clinicianUserId, subject.subjectKey]
              .filter((part) => part.length > 0)
              .join(" | "),
      unreadMessageCount: 0,
      workPhoneNumber: null,
    }));
}

export function sortAdminOperationalSubjectRows(
  rows: AdminOperationalSubjectRow[],
  sortKey: "name" | "site" | "department" | "access" | "role",
) {
  return [...rows].sort((left, right) => {
    const leftValue =
      sortKey === "site" || sortKey === "department"
        ? left.siteLabel
        : sortKey === "access"
          ? left.accessLabel
          : sortKey === "role"
            ? left.roleLabel
            : left.displayName;
    const rightValue =
      sortKey === "site" || sortKey === "department"
        ? right.siteLabel
        : sortKey === "access"
          ? right.accessLabel
          : sortKey === "role"
            ? right.roleLabel
            : right.displayName;

    return (
      leftValue.localeCompare(rightValue) ||
      left.displayName.localeCompare(right.displayName) ||
      left.subjectKey.localeCompare(right.subjectKey)
    );
  });
}

export function resolveAdminPatientOperationsSort(input: "name" | "access"): {
  sortDirection: TenantAdminPatientOperationsSortDirection;
  sortKey: TenantAdminPatientOperationsSortKey;
} {
  if (input === "access") {
    return {
      sortDirection: "desc",
      sortKey: "attention",
    };
  }

  return {
    sortDirection: "asc",
    sortKey: "name",
  };
}

export function resolveAdminClinicianOperationsSort(
  input: "name" | "department" | "role",
): {
  sortDirection: TenantAdminClinicianOperationsSortDirection;
  sortKey: TenantAdminClinicianOperationsSortKey;
} {
  if (input === "department") {
    return {
      sortDirection: "asc",
      sortKey: "department",
    };
  }

  if (input === "role") {
    return {
      sortDirection: "asc",
      sortKey: "role",
    };
  }

  return {
    sortDirection: "asc",
    sortKey: "name",
  };
}

export function sortAdminConnectorRegistrations(
  rows: TenantAdminSummaryResponseV1["summary"]["connectors"]["registrations"],
  sortKey: "name" | "status" | "sync",
) {
  return [...rows].sort((left, right) => {
    if (sortKey === "sync") {
      return (
        resolveAdminTimestampSortValue(right.lastSyncAt) -
          resolveAdminTimestampSortValue(left.lastSyncAt) ||
        left.displayName.localeCompare(right.displayName)
      );
    }

    if (sortKey === "status") {
      return (
        left.status.localeCompare(right.status) ||
        left.displayName.localeCompare(right.displayName)
      );
    }

    return left.displayName.localeCompare(right.displayName);
  });
}

export function sortAdminFeatureFlagRows(
  rows: TenantAdminSummaryFeatureFlagV1[],
  sortKey: "key" | "scope" | "updated",
) {
  return [...rows].sort((left, right) => {
    if (sortKey === "updated") {
      return (
        resolveAdminTimestampSortValue(right.updatedAt) -
          resolveAdminTimestampSortValue(left.updatedAt) ||
        left.flagKey.localeCompare(right.flagKey)
      );
    }

    if (sortKey === "scope") {
      return (
        left.scopeLabel.localeCompare(right.scopeLabel) ||
        left.flagKey.localeCompare(right.flagKey)
      );
    }

    return (
      left.flagKey.localeCompare(right.flagKey) ||
      left.scopeLabel.localeCompare(right.scopeLabel)
    );
  });
}

export function buildAdminWorkflowQueueRows(
  summary: TenantAdminSummaryResponseV1["summary"] | null,
): AdminReviewQueueRow[] {
  return [
    {
      id: "pending-reviews",
      queueLabel: "Pending message reviews",
      count: summary?.workflow.pendingMessageReviewCount ?? 0,
      latestSignalAt: summary?.activity.latestMessageAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestMessageAt ?? null,
      ),
      ownerLabel: "Review ops",
      priorityLabel: "High",
      sourceArea: "Messages",
      supportingText: "Clinician approval queue",
    },
    {
      id: "review-required-tasks",
      queueLabel: "Review-required tasks",
      count: summary?.workflow.reviewRequiredTaskCount ?? 0,
      latestSignalAt: summary?.activity.latestMessageAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestMessageAt ?? null,
      ),
      ownerLabel: "Clinical ops",
      priorityLabel: "High",
      sourceArea: "Tasks",
      supportingText: "Task backlog waiting on review",
    },
    {
      id: "active-assignments",
      queueLabel: "Active assignments",
      count: summary?.workflow.activeTaskAssignmentCount ?? 0,
      latestSignalAt: summary?.activity.latestMessageAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestMessageAt ?? null,
      ),
      ownerLabel: "Care team",
      priorityLabel: "Medium",
      sourceArea: "Assignments",
      supportingText: "Assigned work currently in flight",
    },
    {
      id: "open-conversations",
      queueLabel: "Open conversations",
      count: summary?.workflow.openConversationCount ?? 0,
      latestSignalAt: summary?.activity.latestMessageAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestMessageAt ?? null,
      ),
      ownerLabel: "Inbox",
      priorityLabel: "Medium",
      sourceArea: "Inbox",
      supportingText: "Open conversations requiring attention",
    },
    {
      id: "active-escalations",
      queueLabel: "Open escalations",
      count: summary?.workflow.activeEscalationCount ?? 0,
      latestSignalAt: summary?.activity.latestMessageAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestMessageAt ?? null,
      ),
      ownerLabel: "Ops lead",
      priorityLabel: "High",
      sourceArea: "Escalations",
      supportingText: "Escalated items needing operational follow-up",
    },
    {
      id: "available-documents",
      queueLabel: "Available documents",
      count: summary?.workflow.availableDocumentCount ?? 0,
      latestSignalAt: summary?.activity.latestDocumentAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestDocumentAt ?? null,
      ),
      ownerLabel: "Records",
      priorityLabel: "Medium",
      sourceArea: "Documents",
      supportingText: "Patient-facing and clinician-facing document inventory",
    },
    {
      id: "unmapped-audits",
      queueLabel: "Unmapped audits",
      count: summary?.audit.unmappedResolutionCount ?? 0,
      latestSignalAt: summary?.activity.latestDocumentAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestDocumentAt ?? null,
      ),
      ownerLabel: "Platform",
      priorityLabel: "Medium",
      sourceArea: "Audit",
      supportingText: "Audit items without a mapped resolution",
    },
    {
      id: "fallback-audits",
      queueLabel: "Fallback audits",
      count: summary?.audit.fallbackResolutionCount ?? 0,
      latestSignalAt: summary?.activity.latestDocumentAt ?? null,
      latestSignalText: formatAdminSummaryTimestamp(
        summary?.activity.latestDocumentAt ?? null,
      ),
      ownerLabel: "Platform",
      priorityLabel: "Low",
      sourceArea: "Audit",
      supportingText: "Audit items relying on fallback handling",
    },
  ];
}

// Outbound questionnaire follow-up ("ask only if") authoring helpers. The
// runtime evaluator (evaluateQuestionnaireV1 in @amplecare/contracts) walks
// questions in sequence order and only lets answers of EARLIER visible
// questions open a gate, so the editor must never offer the question itself or
// a later question as a gate candidate.

export type AdminOutboundFollowUpGateQuestionSummary = Pick<
  TenantAdminOutboundQuestionnaireQuestionInputV1,
  "answerType" | "promptText" | "questionKey"
>;

export type AdminOutboundFollowUpTriggerKind =
  | "answered"
  | "equals_no"
  | "equals_value"
  | "equals_yes";

export type AdminOutboundFollowUpGateSelection = {
  equalsText: string;
  gateQuestionKey: string;
  trigger: AdminOutboundFollowUpTriggerKind;
};

export function truncateAdminOutboundFollowUpPromptText(
  value: string,
  maxLength = 60,
): string {
  const normalized = value.trim().replace(/\s+/gu, " ");

  return normalized.length <= maxLength
    ? normalized
    : `${normalized.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`;
}

/**
 * Gate candidates for the question at `questionIndex`: only EARLIER questions
 * (index < questionIndex) qualify, which structurally rules out forward
 * references and self-referencing cycles. Blank and duplicate keys are
 * skipped so the selector never offers an ambiguous gate.
 */
export function buildAdminOutboundFollowUpGateOptions(
  questions: readonly AdminOutboundFollowUpGateQuestionSummary[],
  questionIndex: number,
): Array<{ labelText: string; value: string }> {
  const ownQuestionKey = questions[questionIndex]?.questionKey.trim() ?? "";
  const seenKeys = new Set<string>();
  const options: Array<{ labelText: string; value: string }> = [];

  questions.forEach((question, index) => {
    if (index >= questionIndex) {
      return;
    }

    const questionKey = question.questionKey.trim();

    if (
      questionKey.length === 0 ||
      questionKey === ownQuestionKey ||
      seenKeys.has(questionKey)
    ) {
      return;
    }

    seenKeys.add(questionKey);
    options.push({
      labelText: `${String(index + 1)}. ${
        truncateAdminOutboundFollowUpPromptText(question.promptText) ||
        questionKey
      }`,
      value: questionKey,
    });
  });

  return options;
}

/**
 * Resolves the gate question for a follow-up, searching EARLIER questions
 * only. Returns null when the gate was deleted or reordered below the
 * follow-up, which the editor surfaces as an inline warning instead of
 * crashing.
 */
export function findAdminOutboundFollowUpGateQuestion<
  QuestionValue extends AdminOutboundFollowUpGateQuestionSummary,
>(
  questions: readonly QuestionValue[],
  questionIndex: number,
  gateQuestionKey: string,
): QuestionValue | null {
  const normalizedGateKey = gateQuestionKey.trim();

  if (normalizedGateKey.length === 0) {
    return null;
  }

  const earlierQuestions = questions.slice(
    0,
    Math.max(0, Math.min(questionIndex, questions.length)),
  );

  return (
    earlierQuestions.find(
      (question) => question.questionKey.trim() === normalizedGateKey,
    ) ?? null
  );
}

export function resolveAdminOutboundFollowUpGateSelection(
  skipLogic: TenantAdminOutboundQuestionSkipLogicV1 | null | undefined,
): AdminOutboundFollowUpGateSelection | null {
  const onlyIf = skipLogic?.onlyIf;
  const gateQuestionKey =
    typeof onlyIf?.questionKey === "string" ? onlyIf.questionKey.trim() : "";

  if (onlyIf == null || gateQuestionKey.length === 0) {
    return null;
  }

  if (onlyIf.equals === true) {
    return { equalsText: "", gateQuestionKey, trigger: "equals_yes" };
  }

  if (onlyIf.equals === false) {
    return { equalsText: "", gateQuestionKey, trigger: "equals_no" };
  }

  if (typeof onlyIf.equals === "string" || typeof onlyIf.equals === "number") {
    return {
      equalsText: String(onlyIf.equals),
      gateQuestionKey,
      trigger: "equals_value",
    };
  }

  return { equalsText: "", gateQuestionKey, trigger: "answered" };
}

export type AdminOutboundFollowUpTriggerDescription =
  | {
      gateQuestionKey: string;
      kind: "follow-up";
      /**
       * Chip-ready summary ("Asks when: Yes") when the trigger is cheaply
       * derivable from `equals`/`answerStatus`; null for complex conditions
       * (oneOf/containsAny/valuesAnyOf, non-"answered" statuses, or mixed
       * question + demographic predicates) where the generic "Skip logic"
       * chip stays honest.
       */
      label: string | null;
    }
  | { kind: "demographic" };

/**
 * Read-only classification of a question's `onlyIf` condition for list
 * presentation (Policy step rows). Mirrors the runtime evaluator's
 * semantics: every predicate is ANDed, `questionKey` makes the question a
 * follow-up of an earlier gate, and age/sex predicates alone gate on
 * demographics. Returns null when the condition is absent or empty.
 */
export function describeAdminOutboundFollowUpTrigger(
  onlyIf: TenantAdminOutboundQuestionBranchConditionV1 | null | undefined,
): AdminOutboundFollowUpTriggerDescription | null {
  if (onlyIf == null || typeof onlyIf !== "object") {
    return null;
  }

  const hasDemographicPredicate =
    typeof onlyIf.patientAgeYearsAtLeast === "number" ||
    typeof onlyIf.patientAgeYearsAtMost === "number" ||
    (Array.isArray(onlyIf.patientSexAtBirthOneOf) &&
      onlyIf.patientSexAtBirthOneOf.length > 0) ||
    (Array.isArray(onlyIf.patientSexAtBirthNotOneOf) &&
      onlyIf.patientSexAtBirthNotOneOf.length > 0);
  const gateQuestionKey =
    typeof onlyIf.questionKey === "string" ? onlyIf.questionKey.trim() : "";

  if (gateQuestionKey.length === 0) {
    // anyOf composition (umbrella-flow gates): present it as a follow-up of
    // its first question-keyed sub-condition — the umbrella — with the
    // generic "Skip logic" chip. Without this, the 65 umbrella-gated
    // questions rendered with no gate indication at all.
    const anyOfGateKey = Array.isArray(onlyIf.anyOf)
      ? onlyIf.anyOf
          .map((sub) =>
            typeof sub?.questionKey === "string" ? sub.questionKey.trim() : "",
          )
          .find((subKey) => subKey.length > 0 && subKey !== "medication_system_categories")
      : undefined;

    if (anyOfGateKey) {
      return { gateQuestionKey: anyOfGateKey, kind: "follow-up", label: null };
    }

    return hasDemographicPredicate ? { kind: "demographic" } : null;
  }

  const hasComplexAnswerPredicate =
    (Array.isArray(onlyIf.oneOf) && onlyIf.oneOf.length > 0) ||
    (Array.isArray(onlyIf.containsAny) && onlyIf.containsAny.length > 0) ||
    (Array.isArray(onlyIf.valuesAnyOf) && onlyIf.valuesAnyOf.length > 0) ||
    (onlyIf.answerStatus != null && onlyIf.answerStatus !== "answered");

  if (hasComplexAnswerPredicate || hasDemographicPredicate) {
    return { gateQuestionKey, kind: "follow-up", label: null };
  }

  if (onlyIf.equals === true) {
    return { gateQuestionKey, kind: "follow-up", label: "Asks when: Yes" };
  }

  if (onlyIf.equals === false) {
    return { gateQuestionKey, kind: "follow-up", label: "Asks when: No" };
  }

  if (typeof onlyIf.equals === "string" || typeof onlyIf.equals === "number") {
    const equalsText = truncateAdminOutboundFollowUpPromptText(
      String(onlyIf.equals),
      24,
    );

    return {
      gateQuestionKey,
      kind: "follow-up",
      label:
        equalsText.length > 0
          ? `Asks when: ${equalsText}`
          : "Asks when: answered",
    };
  }

  return { gateQuestionKey, kind: "follow-up", label: "Asks when: answered" };
}

export function buildAdminOutboundFollowUpTriggerOptions(
  gateAnswerType: TenantAdminOutboundQuestionAnswerTypeV1 | null,
): Array<{ labelText: string; value: AdminOutboundFollowUpTriggerKind }> {
  if (gateAnswerType === "yes_no" || gateAnswerType === "confirmation") {
    return [
      { labelText: "Answered Yes", value: "equals_yes" },
      { labelText: "Answered No", value: "equals_no" },
      { labelText: "Answered at all", value: "answered" },
    ];
  }

  const options: Array<{
    labelText: string;
    value: AdminOutboundFollowUpTriggerKind;
  }> = [{ labelText: "Answered at all", value: "answered" }];

  if (gateAnswerType === "single_choice") {
    options.push({ labelText: "Answer equals value", value: "equals_value" });
  }

  return options;
}

/**
 * Builds the next skipLogic value for a follow-up edit, immutably. Clearing
 * the gate ("Always asked") drops `onlyIf` and, for editor-authored
 * questions, returns exactly `{}`; unrelated template-authored keys such as
 * `branches` survive untouched.
 */
export function buildAdminOutboundFollowUpSkipLogic(
  currentSkipLogic: TenantAdminOutboundQuestionSkipLogicV1 | null | undefined,
  update:
    | { gateQuestionKey: null }
    | {
        equalsValue?: string;
        gateQuestionKey: string;
        trigger: AdminOutboundFollowUpTriggerKind;
      },
): TenantAdminOutboundQuestionSkipLogicV1 {
  // The simple gate control can only author {questionKey, equals/answered}.
  // A condition it cannot REPRESENT it must not REWRITE: replacing an
  // umbrella anyOf gate or a demographic predicate (prostate's male-only
  // gate) with a plain gate would silently widen who gets asked. Leave the
  // complex condition untouched — it is authored by the template generator,
  // not this editor.
  const currentOnlyIf = currentSkipLogic?.onlyIf;
  const isComplexCondition =
    currentOnlyIf != null &&
    ((Array.isArray(currentOnlyIf.anyOf) && currentOnlyIf.anyOf.length > 0) ||
      typeof currentOnlyIf.patientAgeYearsAtLeast === "number" ||
      typeof currentOnlyIf.patientAgeYearsAtMost === "number" ||
      (Array.isArray(currentOnlyIf.patientSexAtBirthOneOf) &&
        currentOnlyIf.patientSexAtBirthOneOf.length > 0) ||
      (Array.isArray(currentOnlyIf.patientSexAtBirthNotOneOf) &&
        currentOnlyIf.patientSexAtBirthNotOneOf.length > 0));

  if (isComplexCondition) {
    return { ...(currentSkipLogic ?? {}) };
  }

  const nextSkipLogic: TenantAdminOutboundQuestionSkipLogicV1 = {
    ...(currentSkipLogic ?? {}),
  };

  delete nextSkipLogic.onlyIf;

  if (
    update.gateQuestionKey == null ||
    update.gateQuestionKey.trim().length === 0
  ) {
    return nextSkipLogic;
  }

  const onlyIf: TenantAdminOutboundQuestionBranchConditionV1 = {
    questionKey: update.gateQuestionKey.trim(),
  };

  if (update.trigger === "equals_yes") {
    onlyIf.equals = true;
  } else if (update.trigger === "equals_no") {
    onlyIf.equals = false;
  } else if (update.trigger === "equals_value") {
    onlyIf.equals = update.equalsValue ?? "";
  } else {
    onlyIf.answerStatus = "answered";
  }

  return { ...nextSkipLogic, onlyIf };
}

export function sortAdminReviewQueueRows(
  rows: AdminReviewQueueRow[],
  sortKey: "priority" | "count" | "signal",
) {
  return [...rows].sort((left, right) => {
    if (sortKey === "count") {
      return (
        right.count - left.count ||
        resolveAdminPrioritySortValue(right.priorityLabel) -
          resolveAdminPrioritySortValue(left.priorityLabel) ||
        left.queueLabel.localeCompare(right.queueLabel)
      );
    }

    if (sortKey === "signal") {
      return (
        resolveAdminTimestampSortValue(right.latestSignalAt) -
          resolveAdminTimestampSortValue(left.latestSignalAt) ||
        right.count - left.count ||
        left.queueLabel.localeCompare(right.queueLabel)
      );
    }

    return (
      resolveAdminPrioritySortValue(right.priorityLabel) -
        resolveAdminPrioritySortValue(left.priorityLabel) ||
      right.count - left.count ||
      left.queueLabel.localeCompare(right.queueLabel)
    );
  });
}
